import { Component, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogData } from '@app/new-company/new-company.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Organizations } from '@app/interface/organizations';
import { ReferralRequestInterface } from '@app/interface/referral-request';
import { ToastService } from '@app/services/toast.service';

export interface ReferralRequestDialogData {
  request: ReferralRequestInterface;
  organizationId: number;
  organization: Organizations;
}

@Component({
  selector: 'izy-referral-request-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    // MatDialogClose,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './referral-request-dialog.component.html',
  styleUrl: './referral-request-dialog.component.scss',
})
export class ReferralRequestDialogComponent {
  private toast = inject(ToastService);
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  isLoading = false;

  referralForm: FormGroup;
  referralRequest: ReferralRequestInterface;
  organizationId: number;
  organization: Organizations | null;

  constructor(
    public dialogRef: MatDialogRef<ReferralRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralRequestDialogData,
  ) {
    this.referralForm = new FormGroup({});
    this.referralRequest = data.request;
    this.organizationId = data.organizationId;
    this.organization = data.organization;
  }

  ngOnInit(): void {
    // Initialize form with current subscription data
  }

  getReferralStatusDisplay(value: string | null | undefined): string {
    if (value === null || value === undefined) return 'Non specificato';

    const statusMap: Record<string, string> = {
      PENDING: 'ancora in attesa',
      ACCEPTED: 'stata accettata',
      EXPIRED: 'scaduta',
      FAILED: 'stata rifiutata',
    };

    return statusMap[value] || 'in stato non specificato';
  }

  /** Etichetta breve dello stato, come nella pillola dell'elenco richieste. */
  getReferralStatusLabel(value: string | null | undefined): string {
    if (value === null || value === undefined) return 'Non specificato';

    const labelMap: Record<string, string> = {
      PENDING: 'In attesa',
      ACCEPTED: 'Accettato',
      EXPIRED: 'Scaduto',
      FAILED: 'Rifiutato',
    };

    return labelMap[value] || 'Non specificato';
  }

  /**
   * Tinta della pillola di stato: stessa scala delle chip dell'elenco
   * richieste (accent in attesa, petrol accettata, bianco bordato scaduta,
   * warn rifiutata).
   */
  getStatusPillClass(value: string | null | undefined): string {
    const pillMap: Record<string, string> = {
      PENDING: 'izy-status-pill--pending',
      ACCEPTED: 'izy-status-pill--accepted',
      EXPIRED: 'izy-status-pill--expired',
      FAILED: 'izy-status-pill--failed',
    };

    return (value && pillMap[value]) || 'izy-status-pill--expired';
  }

  copyMailToClipboard(): void {
    navigator.clipboard
      .writeText(this.data.request.mail)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Indirizzo mail copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  copyNumberToClipboard(): void {
    navigator.clipboard
      .writeText(this.data.request.telephone)
      .then(() => {
        // You could show a small confirmation here or rely on the parent
        this.dialogRef.close(false); // Close with success
        this.showSuccessMessage('Numero copiato negli appunti.');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        this.dialogRef.close(false); // Close with failure
      });
  }

  confirm(): void {
    if (this.referralForm.valid) {
      this.dialogRef.close(this.referralForm.value);
    }
  }

  private showSuccessMessage(message: string): void {
    this.toast.success(message);
  }
}
